import type {
  AutocompleteCompanyData,
  CompanyData,
  UpdateBillingEntityInput,
} from "@src/__generated__/graphql";
import { appStore } from "@src/stores/AppStore";
import { multipleEmails, required } from "@src/utils/forms/validators";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

type autocompleteOptions = {
  name: string;
  city: string;
  reg_no: string;
};
export class FormBillingEntity {
  @observable.ref autoCompleteNameOptions: autocompleteOptions[] = [];
  @observable.ref autoCompleteRegistrationNoOptions: autocompleteOptions[] = [];

  legal_name = new FieldState("").validators(required);
  internal_name = new FieldState("").validators(required);
  street = new FieldState("").validators(required);
  city = new FieldState("").validators(required);
  zip = new FieldState("").validators(required);
  country_id = new FieldState("").validators(required);
  registration_no = new FieldState("").validators();
  tax_no = new FieldState("").validators();
  vat_no = new FieldState("").validators();
  payment_due = new FieldState("14").validators();
  invoice_email = new FieldState("").validators(multipleEmails);
  note = new FieldState("").validators();
  applies_vat = new FieldState(true);
  default_currency_id = new FieldState("").validators(required);
  language = new FieldState("").validators(required);
  company_id = new FieldState<string>("").validators(required);
  bank_account_id = new FieldState<string | undefined>(undefined);
  disable_email_notification = new FieldState(false);

  _form = new FormState([
    this.legal_name,
    this.internal_name,
    this.street,
    this.city,
    this.zip,
    this.registration_no,
    this.tax_no,
    this.vat_no,
    this.payment_due,
    this.invoice_email,
    this.note,
    this.country_id,
    this.applies_vat,
    this.default_currency_id,
    this.language,
    this.company_id,
    this.bank_account_id,
    this.disable_email_notification,
  ]);

  constructor() {
    makeObservable(this);
    this.default_currency_id.onChange(
      appStore.workspaceStore.settings?.currency.id ?? "",
    );
    this.language.onChange(
      appStore.workspaceStore.settings?.country.code.toLowerCase() ?? "",
    );
    this.country_id.onChange(
      appStore.workspaceStore.settings?.country.id ?? "",
    );
  }

  serialize(): Omit<UpdateBillingEntityInput, "id"> {
    return {
      legal_name: this.legal_name.$,
      internal_name: this.internal_name.$,
      street: this.street.$,
      city: this.city.$,
      zip: this.zip.$,
      registration_no: this.registration_no.$,
      tax_no: this.tax_no.$,
      vat_no: this.vat_no.$,
      payment_due: +this.payment_due.$,
      invoice_email: this.invoice_email.$,
      note: this.note.$,
      country_id: this.country_id.$,
      applies_vat: this.applies_vat.$,
      default_currency_id: this.default_currency_id.$,
      language: this.language.$,
      workspace_company_id: this.company_id.$,
      workspace_bank_account_id: this.bank_account_id.$ ?? null,
      disable_email_notification: this.disable_email_notification.$,
    };
  }

  public fill(src: CompanyData & { country_id?: string }): void {
    this.legal_name.onChange(src.legal_name ?? "");
    this.street.onChange(src.street ?? "");
    this.city.onChange(src.city ?? "");
    this.zip.onChange(src.zip ?? "");
    this.registration_no.onChange(src.registration_no ?? "");
    this.tax_no.onChange(src.tax_no ?? "");
    this.vat_no.onChange(src.vat_no ?? "");
    this.applies_vat.onChange(src.applies_vat ?? false);
    this.country_id.onChange(src.country_id ?? "");
  }

  @computed get showBankAccountSelect() {
    return (
      appStore.workspaceStore.companyHasMultipleBankAccounts(
        this.company_id.$,
        this.bank_account_id.$,
      ) || this.bank_account_id.hasError
    );
  }

  @action setAutoCompleteNameOptions(src: AutocompleteCompanyData["results"]) {
    this.autoCompleteNameOptions = src.map((i) => ({
      name: i.legal_name,
      city: i.city,
      reg_no: i.national_id,
    })) as autocompleteOptions[];
  }

  @action setAutoCompleteRegistrationNoOptions(
    src: AutocompleteCompanyData["results"],
  ) {
    this.autoCompleteRegistrationNoOptions = src.map((i) => ({
      name: i.legal_name,
      city: i.city,
      reg_no: i.national_id,
    })) as autocompleteOptions[];
  }

  @action setCompanyData(companyData: CompanyData) {
    this.fill({ ...companyData, country_id: companyData.country?.id });
  }

  @action.bound presetBankAccount(companyId: string): void {
    this.bank_account_id.onChange(
      appStore.workspaceStore.getDefaultCompanyBankAccount(companyId)?.id,
    );
  }
}
